import React, { useCallback, useEffect } from 'react'
import { IconinfoCircle, Link as LinkIcon } from '@yy/one-icon'
import { OneModal, OneNotification } from '@yy/one-ui'
import { useTranslation } from 'react-i18next'
import store, { useDispatch, useSelector } from '@common/oneclub/store'
import { readDownload, readMessage } from '@common/oneclub/store/notificationSlice'
import type { DownloadItemType, MessageItemType, UserMessageExtInfoDTO } from '@common/oneclub/store/notificationSlice/type'
import { sleep } from '@common/oneclub/utils'
import Parabola, { uuid } from '@common/oneclub/utils/parabola'
import { type StatusTypes, useBtnConfigs, useGetDownLoadInfo, useGetMessageInfo } from '@common/oneclub/hooks'
import styles from './index.module.less'
import type { ArgsProps } from 'antd/lib/notification'
import { DOWNLOAD_CENTER_BUTTON_TYPE_META, FINGER_PRINT_PUBLIC_KEY_META } from '@common/oneclub/constants'
import { wrapCaptcha } from '@common/oneclub/hooks/useCaptcha'
import { omit } from 'lodash'
import { ModalFuncProps } from 'antd'
import { MESSAGE_RESOURCE } from '@common/oneclub/constants'
import { postUserMessageClosePop, postUserMessageListPopMessage } from '@common/oneclub/services/api/bff/message/api'
import { useMount } from 'ahooks'
import { Provider } from 'react-redux'
import { usePcPasswordExpiredNotif } from '@common/oneclub/hooks/useNotification'

const NotificationFnMap: Record<StatusTypes, (arg: ArgsProps) => void> = {
  success: OneNotification.success,
  fail: OneNotification.error,
  normal: OneNotification.info,
}

/**
 * 抛物线动画
 * @param target 目标元素 id
 * @returns
 */
export function getParabola(target: string) {
  const uuidString = uuid()
  const originId = `o-${uuidString}`

  const getParabolaIns = () => {
    const config = {
      origin: `.${originId}`,
      element: `.${originId}`,
      target,
      defaultStyle: { borderRadius: '4px' },
    }
    const parabolaIns = new Parabola(config)
    return parabolaIns
  }

  /** 开始抛物线动画 */
  const startParabolaAnimation = async () => {
    const parabolIns = getParabolaIns()
    await parabolIns.start()
    parabolIns.destroy()
    parabolIns.removeElement()
  }

  return {
    startParabolaAnimation,
    originId,
    uuid: uuidString,
  }
}

export const usePopNotify = () => {
  const { t } = useTranslation()
  const userInfo = useSelector((state) => state.auth.userInfo)
  const { nickname } = userInfo

  const getBtnConfigs = useBtnConfigs()

  const showPopNotify = useCallback(
    (msgInfo: MessageItemType) => {
      return new Promise<void>((res) => {
        const btnConfigs = getBtnConfigs(msgInfo)
        const confirmProps: ModalFuncProps = {}
        const [btn1, btn2] = btnConfigs.filter((b) => b.show)
        if (btn1) {
          confirmProps.okText = btn1.btnText
          confirmProps.onOk = btn1.onClick
        }
        if (btn2) {
          confirmProps.cancelText = btn2.btnText
          confirmProps.onCancel = btn2.onClick
        }
        const closePop = () => {
          postUserMessageClosePop({ msgSeqs: [msgInfo.msgSeq], appId: 'ONECLUB' })
          res()
        }

        const messageInfo = { title: '', content: '' }
        if (msgInfo.msgSource == MESSAGE_RESOURCE.AR_REMIND) {
          messageInfo.title = t('pop.AR_REMIND.title', { nickname })
          messageInfo.content = t('pop.AR_REMIND.content')
          confirmProps.cancelText = t('frame.confirm')
          confirmProps.onCancel = closePop
          confirmProps.onOk = () => {
            closePop()
            btn1.onClick()
          }
        } else if (msgInfo.msgSource == MESSAGE_RESOURCE.DISABLE_PERMISSION) {
          messageInfo.title = t('pop.DISABLE_PERMISSION.title', { nickname })
          messageInfo.content = t('pop.DISABLE_PERMISSION.content')
          confirmProps.cancelText = t('frame.confirm')
          confirmProps.onCancel = closePop
          confirmProps.onOk = () => {
            closePop()
            btn1.onClick()
          }
        }
        OneModal.confirm({
          title: messageInfo.title,
          content: <div dangerouslySetInnerHTML={{ __html: messageInfo.content }} />,
          icon: <IconinfoCircle style={{ color: '#4B88FF' }} className="anticon" />,
          ...confirmProps,
        })
      })
    },
    [getBtnConfigs, nickname, t]
  )

  usePcPasswordExpiredNotif()

  // 建议放到@common/oneclub/hooks/useNotification里面
  useMount(async () => {
    // 弹窗提示
    const res = await postUserMessageListPopMessage({ appId: 'ONECLUB', pageNum: 1, pageSize: 100 })
    if (res.data?.length) {
      for (let index = 0; index < res.data?.length; index++) {
        const msgInfo = res.data[index]
        await showPopNotify({ ...msgInfo, extInfo: (msgInfo.extInfo ? JSON.parse(msgInfo.extInfo) : {}) as UserMessageExtInfoDTO })
      }
    }
  })
}

interface MessageNotificationProps {
  /** 通知消息内容项 */
  messageItem: MessageItemType
}

/**
 * 消息中心通知
 * messageItem 改变，证明有新消息
 */
export const MessageNotification: React.FC<MessageNotificationProps> = React.memo(
  ({ messageItem }) => {
    const dispatch = useDispatch()

    const time = 5000

    const { messageInfo, messageStatus, btnConfigs } = useGetMessageInfo({ messageItem })

    const { title, content, content1 } = messageInfo

    useEffect(() => {
      ;(async () => {
        if (!messageItem) return

        const key = messageItem.msgSeq

        if (messageItem.read) {
          OneNotification.close(key)
          return
        }

        const { originId, startParabolaAnimation } = getParabola('#notify-message-button')

        let hasClosed = false
        // 已读
        const hasRead = () => {
          dispatch(readMessage(messageItem.msgSeq))
          OneNotification.close(key)
          hasClosed = true
        }

        const config: ArgsProps = {
          key,
          duration: 0,
          className: originId,
          message: <div className={styles.title}>{title}</div>,
          onClose: hasRead,
          description: (
            <Provider store={store}>
              <div className={styles.content}>{content}</div>
              {content && content1 && <div className={styles.content}>{content1}</div>}
              {btnConfigs.length > 0 && (
                <div className={styles.btn}>
                  {btnConfigs.map((btn) => (
                    <span
                      key={btn.btnText}
                      onClick={() => {
                        btn.onClick()
                        hasRead()
                      }}
                    >
                      {btn.btnText}
                      <LinkIcon style={{ marginLeft: 4 }} />
                    </span>
                  ))}
                </div>
              )}
            </Provider>
          ),
        }

        const Notification = NotificationFnMap[messageStatus]
        Notification(config)

        await sleep(+time)

        if (!hasClosed) {
          await startParabolaAnimation()
          OneNotification.close(key)
        }
      })()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, messageItem])

    return <></>
  },
  (preProps, nextProps) => {
    if (preProps.messageItem?.msgSeq !== nextProps.messageItem?.msgSeq) {
      return false
    }
    return true
  }
)

interface DownLoadNotificationProps {
  /** 下载消息内容项 */
  messageItem: DownloadItemType
}

/**
 * 下载中心通知
 */
export const DownLoadNotification: React.FC<DownLoadNotificationProps> = React.memo(
  ({ messageItem }) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const time = 5000

    const { messageInfo, messageStatus, btnConfigs } = useGetDownLoadInfo({ item: messageItem })

    const { title, content } = messageInfo

    useEffect(() => {
      ;(async () => {
        if (!messageItem) return

        const key = `open_${Date.now()}`

        const { originId, startParabolaAnimation } = getParabola('#parabola-export')

        let hasClosed = false
        // 已读
        const hasRead = async () => {
          await dispatch(readDownload(messageItem.taskId))
          OneNotification.close(key)
          hasClosed = true
        }

        const config: ArgsProps = {
          key,
          duration: 0,
          className: originId,
          message: <div className={styles.title}>{t(title)}</div>,
          onClose: hasRead,
          description: (
            <Provider store={store}>
              <div className={styles.content}>{t(content)}</div>
              {btnConfigs.length > 0 && (
                <div className={styles.btn}>
                  {btnConfigs.map((btn) => {
                    if (btn.buttonType === DOWNLOAD_CENTER_BUTTON_TYPE_META.DOWNLOAD) {
                      const CaptchaButton = wrapCaptcha((props: any) => <span {...omit(props, ['loading'])} />)
                      return (
                        <CaptchaButton
                          key={btn.btnText}
                          onCaptchaClick={async (e, token) => {
                            await btn.onClick(e, token)
                            await hasRead()
                          }}
                          publicKey={FINGER_PRINT_PUBLIC_KEY_META.DOWNLOAD_CENTER}
                        >
                          {btn.btnText}
                          <LinkIcon style={{ marginLeft: 4 }} />
                        </CaptchaButton>
                      )
                    }
                    return (
                      <span
                        key={btn.btnText}
                        onClick={(e) => {
                          btn.onClick(e)
                          hasRead()
                        }}
                      >
                        {btn.btnText}
                        <LinkIcon style={{ marginLeft: 4 }} />
                      </span>
                    )
                  })}
                </div>
              )}
            </Provider>
          ),
        }

        const Notification = NotificationFnMap[messageStatus]

        // 通知
        Notification(config)

        await sleep(+time)

        if (!hasClosed) {
          await startParabolaAnimation()
          OneNotification.close(key)
        }
      })()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, messageItem])

    return <></>
  },
  (preProps, nextProps) => {
    if (preProps.messageItem?.taskId !== nextProps.messageItem?.taskId) {
      return false
    }
    return true
  }
)
